/**
 * ShieldWall — Honeypot Trap
 *
 * An invisible form field positioned off-screen. Bots auto-fill it
 * via autofill heuristics or form-scanning logic; humans never see it.
 *
 * @module useHoneypot
 */

import { useRef } from "react";

export interface HoneypotProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  isTriggered: () => boolean;
}

/**
 * Hook that manages an invisible honeypot field.
 * If the field is filled or focused, the user is likely a bot.
 *
 * Usage:
 * ```tsx
 * const honeypot = useHoneypot();
 *
 * // In JSX (hidden from humans):
 * <div style={{ position: 'absolute', left: -9999 }} aria-hidden="true">
 *   <input name="website_url" tabIndex={-1} autoComplete="off"
 *     onChange={honeypot.onChange} onFocus={honeypot.onFocus} />
 * </div>
 *
 * // During verification:
 * if (honeypot.isTriggered()) { /* bot detected *\/ }
 * ```
 */
export function useHoneypot(): HoneypotProps {
  const value = useRef("");
  const focused = useRef(false);

  return {
    onChange: (e) => {
      value.current = e.target.value;
    },
    onFocus: () => {
      focused.current = true;
    },
    isTriggered: () => value.current.length > 0 || focused.current,
  };
}
